import { Cause, Context, Effect, Layer, Ref, type Scope } from "effect";
import { boundedReviewDiffContents, reviewDiffLanguage, reviewDiffModelUri } from "@workerstation/shared";
import type { CodeDiffConfiguration, CodeDiffEditorMode, CodeDiffSnapshot } from "./diff-model.ts";
import { DiffEditorIo, type CodeDiffEditorPropsCallback } from "./diff-editor-io.ts";
import { ImmutableModels } from "./models-service.ts";
import { immutableModelDependencyFailure } from "./model.ts";
import { makeImmutableRegistry } from "./registry.ts";
import type { ImmutableLease, ImmutableRegistryFailure } from "./registry-model.ts";

export interface DiffEditorSession {
  readonly update: (configuration: CodeDiffConfiguration) => Effect.Effect<{ readonly message: string | null }, ImmutableRegistryFailure, Scope.Scope>;
  readonly configure: (configuration: CodeDiffConfiguration) => Effect.Effect<void, ImmutableRegistryFailure>;
}
export class DiffEditors extends Context.Service<DiffEditors, {
  readonly acquire: (key: string, container: HTMLElement, mode: CodeDiffEditorMode, label: string, mounted: CodeDiffEditorPropsCallback) => Effect.Effect<ImmutableLease<DiffEditorSession>, ImmutableRegistryFailure, Scope.Scope>;
}>()("workerstation/frontend/DiffEditors") {}

const sameSelection = (selected: CodeDiffConfiguration | undefined, requested: CodeDiffConfiguration): boolean => selected !== undefined
  && selected.candidateId === requested.candidateId && selected.active === requested.active
  && selected.original.treeId === requested.original.treeId && selected.original.workspaceId === requested.original.workspaceId
  && selected.original.path === requested.original.path && selected.original.contents === requested.original.contents
  && selected.modified.treeId === requested.modified.treeId && selected.modified.workspaceId === requested.modified.workspaceId
  && selected.modified.path === requested.modified.path && selected.modified.contents === requested.modified.contents;

export const DiffEditorsLive = (owner: Scope.Scope) => Layer.effect(DiffEditors, Effect.gen(function* () {
  const io = yield* DiffEditorIo, models = yield* ImmutableModels;
  const cleanupFailures = yield* Ref.make<readonly Cause.Cause<never>[]>([]);
  const retainCleanupFailure = (cause: Cause.Cause<never>) => Ref.update(cleanupFailures, causes => [...causes, cause]);
  // Atom disposal can finish before root closure. Preserve its owned cleanup
  // receipt after the failed lease leaves the registry, then report it once.
  yield* Effect.addFinalizer(() => Effect.gen(function* () {
    const reasons = (yield* Ref.getAndSet(cleanupFailures, [])).flatMap(cause => cause.reasons);
    if (reasons.length) return yield* Effect.failCause(Cause.fromReasons(reasons));
  }));
  const editors = yield* makeImmutableRegistry<DiffEditorSession>(owner, retainCleanupFailure);
  return DiffEditors.of({
    acquire: Effect.fn("Frontend.DiffEditors.acquire")((key, container, mode, label, mounted) => editors.acquire({
      key, contents: "",
      create: Effect.gen(function* () {
        const editor = yield* io.acquire(container, mode, label);
        // This dependent registry closes bindings and joins their accepted
        // acquisitions before the editor's own platform cleanup runs.
        const bindings = yield* makeImmutableRegistry<void>(owner, retainCleanupFailure);
        const revision = yield* Ref.make(0);
        const view = yield* Ref.make<CodeDiffConfiguration | undefined>(undefined);
        yield* io.mounted(editor, mounted);
        const request = (side: "base" | "candidate", candidateId: string, snapshot: CodeDiffSnapshot) => Effect.try({
          try: () => ({ identity: reviewDiffModelUri({ side, candidateId, snapshotTreeId: snapshot.treeId, workspaceId: snapshot.workspaceId, path: snapshot.path }), contents: boundedReviewDiffContents(snapshot.contents), languageId: reviewDiffLanguage(snapshot.path) }),
          catch: immutableModelDependencyFailure
        });
        return {
          configure: Effect.fn("Frontend.DiffEditors.configure")((configuration) => Effect.gen(function* () {
            if (owner.state._tag === "Closed") return yield* Effect.interrupt;
            yield* Ref.set(view, configuration);
            const current = () => owner.state._tag !== "Closed" && Ref.getUnsafe(view) === configuration;
            yield* io.configure(editor, configuration, current);
            if (configuration.active && configuration.revealLine !== undefined) yield* io.reveal(editor, configuration.revealLine, current);
          })),
          update: Effect.fn("Frontend.DiffEditors.update")((configuration) => Effect.gen(function* () {
            const token = yield* Ref.updateAndGet(revision, value => value + 1);
            yield* Ref.set(view, configuration);
            const ownsBinding = () => Ref.getUnsafe(revision) === token;
            const current = () => owner.state._tag !== "Closed" && ownsBinding() && sameSelection(Ref.getUnsafe(view), configuration);
            yield* io.configure(editor, configuration, () => current() && Ref.getUnsafe(view) === configuration);
            yield* io.models(editor, null, current);
            if (!configuration.active || !current()) return { message: null };
            yield* bindings.acquire({ key: String(token), contents: "", create: Effect.gen(function* () {
              const original = yield* models.acquire(yield* request("base", configuration.candidateId, configuration.original));
              const modified = yield* models.acquire(yield* request("candidate", configuration.candidateId, configuration.modified));
              // Register after both model leases so detach runs before release.
              // During root closure the latest binding still owns that detach.
              yield* Effect.addFinalizer(() => io.models(editor, null, ownsBinding).pipe(Effect.orDie));
              yield* io.models(editor, { original: original.model.model, modified: modified.model.model }, current);
              const latestView = yield* Ref.get(view);
              if (latestView?.revealLine !== undefined) yield* io.reveal(editor, latestView.revealLine, current);
            }) });
            return { message: null };
          }).pipe(Effect.catchTags({
            ImmutableModelConflict: error => Effect.succeed({ message: error.message }),
            ImmutableModelAdmissionFailure: error => Effect.succeed({ message: error.message })
          })))
        } satisfies DiffEditorSession;
      })
    }))
  });
}));
